import React, { useState, useContext, createContext, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import ButtonSecondary from '../ButtonSecondary';
import { useTranslation } from 'next-i18next';
import { Button } from 'ariakit/button';

import {
  GradientContext,
  ContactContext,
  ZoomLevelContext,
} from '../../containers/BasePage';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import s from './NavigationMobile.module.scss';
import ButtonBurger from '../ButtonBurger/ButtonBurger';
import { trackMenuInteraction, trackMenuContact } from '../../utils/dataLayer';

const NavContext = createContext();

const NavHeader = ({ isOpen, onClick, force, languages = [] }) => {
  const { t } = useTranslation();
  const [hasAnimated, setHasAnimated] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleLanguageDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleLanguageKeyDown = (e) => {
    if (e.key === 'Escape') setDropdownOpen(false);
  };

  return (
    <div className={s.NavHeader}>
      <div
        className={classNames(s.LogoWrapper, {
          [s.Hide]: isOpen,
          [s.Show]: !isOpen,
        })}>
        <Link href="/" className={s.LogoLink}>
          <Image
            width={151}
            height={36}
            src="/img/logo-mobile.svg"
            alt="Stockholm - capital of Scandinavia logotype, click to go to Start"
            className={s.Logo}
          />
        </Link>
      </div>

      <div
        className={classNames(s.MenuText, {
          [s.Show]: isOpen,
          [s.Hide]: !isOpen,
        })}>
        {t('Common.menu')}
      </div>

      <div className={s.NavHeaderRight}>
        {/* Hide until the english version is ready */}
        <div
          className={classNames(s.Languages, {
            [s.IsVisible]: isOpen,
          })}>
          <div
            className={classNames(s.LanguageWrapper, {
              [s.IsOpen]: dropdownOpen,
            })}>
            <button
              type="button"
              className={classNames(s.LanguageDropdown, {
                [s.IsOpen]: dropdownOpen,
              })}
              onClick={toggleLanguageDropdown}
              onKeyDown={handleLanguageKeyDown}
              aria-expanded={dropdownOpen}
              aria-haspopup="true">
              <Image width={16} height={16} src={`/img/icon-web.svg`} alt="" />
              <span className={s.LanguageText}>
                {languages.find((lang) => lang.active)?.title ||
                  'Select Language'}
              </span>
              <Image
                className={s.LanguageChevron}
                width={16}
                height={16}
                src={`/img/icon-chevron-down.svg`}
                alt=""
              />
            </button>
            {dropdownOpen && (
              <div className={s.LanguageList}>
                {languages
                  .filter((lang) => !lang.active)
                  .map((lang) => (
                    <Link key={lang.lang_code} href={lang.href}>
                      <span className={s.LanguageItem}>{lang.title}</span>
                    </Link>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* === Burger-knappen === */}
        <button
          onClick={() => {
            onClick();
            if (!isOpen) {
              setTimeout(() => {
                setHasAnimated(true);
              }, 450);
            } else {
              setHasAnimated(false);
            }
          }}
          type="button"
          aria-label={`${isOpen ? 'Close' : 'Open'} menu`}
          className={s.Burger}>
          <ButtonBurger
            isOpen={isOpen}
            forceOpen={force || (hasAnimated && isOpen)}
          />
        </button>
      </div>
    </div>
  );
};

const NavigationMobile = ({
  navigation = [],
  insights = {},
  languages = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const gradient = useContext(GradientContext);
  const browserZoomPercentage = useContext(ZoomLevelContext);

  const rootClasses = classNames(s.Root, { [s.Open]: isOpen });
  const overlayClasses = classNames(s.Overlay, { [s.Open]: isOpen });

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }, [isOpen]);

  return (
    <header className={rootClasses}>
      <nav className={s.NavigationMobile} aria-label="Meny">
        <NavHeader
          isOpen={isOpen}
          onClick={() => setIsOpen((pv) => !pv)}
          languages={languages}
        />

        <div className={overlayClasses}>
          <div className={classNames(s.TopBlob, s[gradient])} />
          <div className={s.DialogContainer}>
            <div className={s.LinkContainer}>
              {navigation.map(({ title, href, children }, index) => {
                if (!children || children.length === 0) {
                  return (
                    <a
                      key={href || index}
                      href={href}
                      v
                      className={s.NavigationMobileLink}>
                      {title}
                    </a>
                  );
                } else {
                  return (
                    <MegaMenu
                      browserZoomPercentage={browserZoomPercentage}
                      key={href || index}
                      href={href}
                      title={title}
                      navChildren={children}
                      insights={insights}
                    />
                  );
                }
              })}
            </div>

            {browserZoomPercentage === 100 && <Contact />}
          </div>
        </div>
      </nav>
    </header>
  );
};

NavigationMobile.propTypes = {
  navigation: PropTypes.array.isRequired,
};

NavigationMobile.defaultProps = { navigation: [] };

const MegaMenu = ({ title, href, browserZoomPercentage, navChildren = [] }) => {
  const { t } = useTranslation();
  const gradient = useContext(GradientContext);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={s.MegaMenuContainer}>
      <Button
        className={s.MegaMenuButton}
        onClick={() => {
          setIsOpen(true);
          trackMenuInteraction(1, title);
        }}>
        {title}
        <Image
          width={24}
          height={24}
          src="/img/icon-arrow-right-white.svg"
          alt=""
          className={s.Arrow}
        />
      </Button>
      <div className={classNames(s.MegaMenu, { [s.IsOpen]: isOpen })}>
        <div className={classNames(s.TopBlob, s[gradient])} />
        {isOpen && (
          <div className={s.Container}>
            <div className={s.DialogContainer}>
              <Button
                className={s.MegaMenuMenuButton}
                onClick={() => setIsOpen(false)}>
                <Image
                  width={24}
                  height={24}
                  src="/img/icon-arrow-right-white.svg"
                  alt=""
                  className={s.ArrowReverse}
                />
                {t('Common.back')}
              </Button>
              {href ? (
                <a href={href} className={s.MegaMenuChildTitle}>
                  {title}
                </a>
              ) : (
                <span className={s.MegaMenuChildTitle}>{title}</span>
              )}

              <div className={s.MegaMenuLinks}>
                {navChildren.map((section, index) => (
                  <div key={index} className={s.MegaMenuLinkItem}>
                    {section.href ? (
                      <a
                        href={section.href}
                        className={s.MegaMenuLinkGrandChildTitle}>
                        {section.title}
                      </a>
                    ) : (
                      <span className={s.MegaMenuLinkGrandChildTitle}>
                        {section.title}
                      </span>
                    )}
                    <div className={s.MegaMenuGrandChildren}>
                      {section.children.map((child, index) => (
                        <a
                          key={index}
                          href={child.href}
                          onClick={() => {
                            trackMenuInteraction(2, child.title);
                          }}
                          className={s.MegaMenuLink}>
                          {child.title}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {browserZoomPercentage === 100 && <Contact />}
          </div>
        )}
      </div>
    </div>
  );
};

const Contact = () => {
  const contact = useContext(ContactContext);
  const gradient = useContext(GradientContext);

  if (!contact?.url) return null;

  return (
    <div className={classNames(s.MegaMenuContact, s[gradient])}>
      <div className={s.ContactWrap}>
        <div className={s.ContactTitle}>Contact</div>
        <div className={s.ContactText}>
          We are here to help you on your Stockholm business journey.
        </div>
      </div>
      <ButtonSecondary
        borderColor="white"
        asLink
        onClick={trackMenuContact}
        href={contact.url}
        modifiers={['Circle']}>
        <Image
          width={26}
          height={20}
          src="/img/icon-arrow-right-white.svg"
          alt=""
          className={s.Contact}
        />
      </ButtonSecondary>
    </div>
  );
};

export default NavigationMobile;
